import { Link, useLocation } from 'react-router-dom'
import {
  ArrowsClockwise,
  ChartBar,
  ClipboardText,
  Cube,
  DownloadSimple,
  List,
  Sparkle,
  Truck,
  UploadSimple,
} from '@/components/icons'
import { useCanAdministerSyncs, useCanManageMsl, useCanWriteOms } from '@/app/auth/use-pulse-me'
import { JIT_UI_ENABLED, OMS_WRITES_ENABLED } from '@/lib/env'
import { cn } from '@/lib/cn'
import pulseLogo from '@/assets/pulse-logo.svg'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { UserMenu } from './user-menu'

export interface NavItem {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  /** Which URLs light this item up. Defaults to an exact match on `to`. */
  matches?: (pathname: string) => boolean
}

// Sub-routes of /pos that belong to a different nav item than POs.
const PO_TOOL_PREFIXES = ['/pos/fulfillable', '/pos/batches', '/pos/bulk-allocate', '/pos/upload']

const PURCHASE_ORDERS: NavItem = {
  to: '/pos',
  label: 'POs',
  icon: ClipboardText,
  // The PO detail (/pos/:externalOrderNumber) is still "POs"; the tooling
  // under /pos/* is not.
  matches: pathname =>
    (pathname === '/pos' || pathname.startsWith('/pos/')) &&
    !PO_TOOL_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)),
}
// The queue is a read for everyone, so this sits beside POs rather than
// behind the OMS write gate. Cancel lives on the detail, Admin+ role only.
const SALES_ORDERS: NavItem = {
  to: '/sales-orders',
  label: 'SOs',
  icon: Truck,
  matches: pathname => pathname === '/sales-orders' || pathname.startsWith('/sales-orders/'),
}
const JIT_PURCHASE_ORDERS: NavItem = {
  to: '/jit-pos',
  label: 'JIT POs',
  icon: Sparkle,
  matches: pathname => pathname === '/jit-pos' || pathname.startsWith('/jit-pos/'),
}
const REPORTS: NavItem = {
  to: '/reports',
  label: 'Reports',
  icon: ChartBar,
  matches: pathname => pathname === '/reports' || pathname.startsWith('/reports/'),
}
const FULFILLABLE: NavItem = {
  to: '/pos/fulfillable',
  label: 'Bulk Fulfill',
  icon: Cube,
  // Bulk allocation and its batch history are the workflow this queue feeds.
  matches: pathname =>
    ['/pos/fulfillable', '/pos/batches', '/pos/bulk-allocate'].some(
      prefix => pathname === prefix || pathname.startsWith(`${prefix}/`),
    ),
}
const UPLOAD: NavItem = { to: '/pos/upload', label: 'Upload PO', icon: UploadSimple }
const MSL: NavItem = {
  to: '/upload',
  label: 'MSL',
  icon: DownloadSimple,
  matches: pathname => pathname === '/upload' || pathname.startsWith('/upload/'),
}
const CHANNEL_SYNCS: NavItem = {
  to: '/admin/channel-syncs',
  label: 'Sync',
  icon: ArrowsClockwise,
}

/**
 * Every navigable surface in Pulse, gated by the same role checks the routes
 * apply. This replaces both the sidebar and the ⌘K palette, so anything either
 * of them could reach must be reachable here.
 *
 * One flat row: Reports is for everyone, MSL only for Config Manager+.
 */
export function useNavItems(): NavItem[] {
  const canAdministerSyncs = useCanAdministerSyncs()
  const canManageMsl = useCanManageMsl()
  const canWriteOms = useCanWriteOms()
  return [
    PURCHASE_ORDERS,
    SALES_ORDERS,
    ...(JIT_UI_ENABLED && canWriteOms ? [JIT_PURCHASE_ORDERS] : []),
    ...(canWriteOms ? [FULFILLABLE] : []),
    ...(OMS_WRITES_ENABLED && canWriteOms ? [UPLOAD] : []),
    REPORTS,
    ...(canManageMsl ? [MSL] : []),
    ...(canAdministerSyncs ? [CHANNEL_SYNCS] : []),
  ]
}

function isActive(item: NavItem, pathname: string): boolean {
  return item.matches ? item.matches(pathname) : pathname === item.to
}

export function TopBar() {
  const items = useNavItems()
  const { pathname } = useLocation()

  return (
    <header className="sticky top-0 z-30 bg-[color:var(--card)] pt-[env(safe-area-inset-top)] shadow-[var(--shadow-raised)]">
      <div className="mx-auto flex h-16 w-full max-w-[1600px] items-center gap-2 px-4 md:gap-6 md:px-8 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]">
        <MobileNav items={items} pathname={pathname} />

        <Link
          to="/pos"
          className="flex shrink-0 items-center rounded-md text-[color:var(--foreground)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--ring)]"
          aria-label="Pulse home"
        >
          <img src={pulseLogo} alt="Pulse" draggable={false} className="h-7 w-auto" />
        </Link>

        {/* Items run left to right after the logo. If a role unlocks more than
            the bar can hold, the row scrolls sideways and fades at the edge
            instead of pushing anything under the user menu. */}
        <nav aria-label="Primary navigation" className="hidden min-w-0 flex-1 md:block">
          <ul className="flex items-center gap-1 overflow-x-auto [mask-image:linear-gradient(to_right,black_calc(100%-2rem),transparent)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden pr-8">
            {items.map(item => (
              <li key={item.to} className="shrink-0">
                <NavTab item={item} pathname={pathname} />
              </li>
            ))}
          </ul>
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          <UserMenu />
        </div>
      </div>
    </header>
  )
}

function NavTab({ item, pathname }: { item: NavItem; pathname: string }) {
  const Icon = item.icon
  const active = isActive(item, pathname)
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'inline-flex h-10 items-center gap-1.5 rounded-lg px-2.5 text-sm whitespace-nowrap',
        'transition-colors duration-[130ms]',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-[color:var(--ring)]',
        active
          ? 'bg-[color:var(--muted)] font-semibold text-[color:var(--foreground)]'
          : 'font-medium text-[color:var(--muted-foreground)] hover:bg-[color:var(--muted)]/60 hover:text-[color:var(--foreground)]',
      )}
    >
      <Icon className="size-[18px] shrink-0" />
      <span>{item.label}</span>
    </Link>
  )
}

/** Below `md`, the same items collapse into a menu behind a single button. */
function MobileNav({ items, pathname }: { items: NavItem[]; pathname: string }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex size-11 items-center justify-center rounded-lg text-[color:var(--foreground)] hover:bg-[color:var(--muted)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--ring)] md:hidden"
        aria-label="Open navigation"
      >
        <List className="size-5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        {items.map(item => {
          const Icon = item.icon
          const active = isActive(item, pathname)
          return (
            <DropdownMenuItem key={item.to} asChild>
              <Link
                to={item.to}
                aria-current={active ? 'page' : undefined}
                className={cn('min-h-11', active && 'bg-[color:var(--muted)] font-semibold')}
              >
                <Icon className="size-4" />
                {item.label}
              </Link>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
